import {
  AVAILABLE_READING_FORMS,
  READING_APPETITES,
  READING_ARTICULATIONS,
  READING_BY_ID,
  READING_COMMITMENTS,
  READING_DIFFICULTIES,
  READING_INTENTS,
} from "../content/catalog";
import {
  BOUNDARY_STUDY,
  EXPRESSION_INTENTIONS,
  EXPRESSION_RELATIONSHIPS,
  EXPRESSION_RISKS,
} from "../content/expression-studies";
import { EXPRESSION_STUDY_BY_ID } from "../content/expression-study-catalog";
import type {
  ExpressionIntention,
  ExpressionRelationship,
  ExpressionRisk,
} from "../content/expression-studies";
import type {
  ReadingAppetite,
  ReadingArticulation,
  ReadingCommitment,
  ReadingForm,
  ReadingIntent,
  ReadingLexicalDifficulty,
} from "../content/types";
import type {
  CommandVocabularyArticulation,
  CommandVocabularyHandle,
  CommandVocabularyRegister,
} from "../content/command-vocabulary";
import type { CommandVocabularyDifficulty } from "./command-vocabulary-catalog";

export type ShareableView = "home" | "read" | "practice" | "learning";
export type ShareablePracticeView = "overview" | "discover" | "words" | "expression";
export type ShareableLearningSection =
  | "overview"
  | "readings"
  | "words"
  | "expression"
  | "progress"
  | "profile"
  | "membership"
  | "data";

export type ShareableLocation = {
  view: ShareableView;
  workId: string | null;
  browse: boolean;
  particular: boolean;
  commitment: ReadingCommitment;
  appetite: ReadingAppetite;
  difficulty: ReadingLexicalDifficulty | null;
  intent: ReadingIntent | null;
  form: ReadingForm | null;
  articulation: ReadingArticulation | null;
  practiceView: ShareablePracticeView;
  learningSection: ShareableLearningSection;
  wordId: string | null;
  relatedWordId: string | null;
  wordQuery: string;
  wordInitial: string;
  wordHandle: CommandVocabularyHandle | "all";
  wordLevel: CommandVocabularyDifficulty | "all";
  wordRegister: CommandVocabularyRegister | "all";
  wordArticulation: CommandVocabularyArticulation | "all";
  studyId: string;
  stanceId: string;
  expressionIntention: ExpressionIntention;
  expressionRelationship: ExpressionRelationship | "all";
  expressionRisk: ExpressionRisk | "all";
};

export const DEFAULT_SHAREABLE_LOCATION: ShareableLocation = {
  view: "home",
  workId: null,
  browse: false,
  particular: false,
  commitment: "brief",
  appetite: "surprise",
  difficulty: null,
  intent: null,
  form: null,
  articulation: null,
  practiceView: "overview",
  learningSection: "overview",
  wordId: null,
  relatedWordId: null,
  wordQuery: "",
  wordInitial: "all",
  wordHandle: "all",
  wordLevel: "all",
  wordRegister: "all",
  wordArticulation: "all",
  studyId: BOUNDARY_STUDY.id,
  stanceId: "tactful",
  expressionIntention: BOUNDARY_STUDY.route.intention,
  expressionRelationship: "all",
  expressionRisk: "all",
};

export const SHAREABLE_QUERY_KEYS = [
  "view",
  "work",
  "browse",
  "particular",
  "time",
  "mood",
  "difficulty",
  "intent",
  "form",
  "landing",
  "practice",
  "section",
  "word",
  "related",
  "word-query",
  "word-initial",
  "word-purpose",
  "word-level",
  "register",
  "word-landing",
  "study",
  "stance",
  "expression-intention",
  "expression-relationship",
  "expression-risk",
] as const;

function oneOf<T extends string>(
  value: string | null,
  options: readonly T[],
  fallback: T,
) {
  return value && options.includes(value as T) ? value as T : fallback;
}

function optionalOneOf<T extends string>(
  value: string | null,
  options: readonly T[],
) {
  return value && options.includes(value as T) ? value as T : null;
}

export function parseShareableLocation(href: string): ShareableLocation {
  const url = new URL(href, "https://verbalore.com/");
  const legacyView = url.hash === "#read"
    ? "read"
    : url.hash === "#practice"
      ? "practice"
      : "home";
  const view = oneOf(
    url.searchParams.get("view"),
    ["home", "read", "practice", "learning"] as const,
    legacyView,
  );
  const work = url.searchParams.get("work");
  const studyId = oneOf(
    url.searchParams.get("study"),
    [...EXPRESSION_STUDY_BY_ID.keys()],
    BOUNDARY_STUDY.id,
  );
  const study = EXPRESSION_STUDY_BY_ID.get(studyId) || BOUNDARY_STUDY;

  return {
    view,
    workId: work && READING_BY_ID.has(work) ? work : null,
    browse: view === "read" && url.searchParams.get("browse") === "1",
    particular:
      view === "read"
      && url.searchParams.get("browse") === "1"
      && url.searchParams.get("particular") === "1",
    commitment: oneOf(
      url.searchParams.get("time"),
      READING_COMMITMENTS.map(({ id }) => id),
      "brief",
    ),
    appetite: oneOf(
      url.searchParams.get("mood"),
      READING_APPETITES.map(({ id }) => id),
      "surprise",
    ),
    difficulty: optionalOneOf(
      url.searchParams.get("difficulty"),
      READING_DIFFICULTIES.map(({ id }) => id),
    ),
    intent: optionalOneOf(
      url.searchParams.get("intent"),
      READING_INTENTS.map(({ id }) => id),
    ),
    form: optionalOneOf(url.searchParams.get("form"), AVAILABLE_READING_FORMS),
    articulation: optionalOneOf(
      url.searchParams.get("landing"),
      READING_ARTICULATIONS.map(({ id }) => id),
    ),
    practiceView: oneOf(
      url.searchParams.get("practice"),
      ["overview", "discover", "words", "expression"] as const,
      "overview",
    ),
    learningSection: oneOf(
      url.searchParams.get("section"),
      ["overview", "readings", "words", "expression", "progress", "profile", "membership", "data"] as const,
      "overview",
    ),
    wordId: /^[a-z][a-z'-]{1,79}$/.test(url.searchParams.get("word") || "")
      ? url.searchParams.get("word")
      : null,
    relatedWordId: /^[a-z][a-z'-]{1,79}$/.test(url.searchParams.get("related") || "")
      ? url.searchParams.get("related")
      : null,
    wordQuery: (url.searchParams.get("word-query") || "")
      .trim()
      .replace(/\s+/g, " ")
      .slice(0, 80),
    wordInitial: /^[A-Z]$/.test(url.searchParams.get("word-initial") || "")
      ? url.searchParams.get("word-initial")!
      : "all",
    wordHandle: oneOf(
      url.searchParams.get("word-purpose"),
      ["all", "think-precisely", "speak-with-confidence", "understand-people", "write-memorably"] as const,
      "all",
    ),
    wordLevel: oneOf(
      url.searchParams.get("word-level"),
      ["all", "foundation", "bright-frontier", "mid-frontier", "deep-frontier"] as const,
      "all",
    ),
    wordRegister: oneOf(
      url.searchParams.get("register"),
      ["all", "conversational", "professional", "formal", "literary", "rhetorical"] as const,
      "all",
    ),
    wordArticulation: oneOf(
      url.searchParams.get("word-landing"),
      ["all", "direct", "warm-authority", "restrained", "tactful", "punchy"] as const,
      "all",
    ),
    studyId,
    stanceId: oneOf(
      url.searchParams.get("stance"),
      study.stances.map(({ id }) => id),
      study.stances.some(({ id }) => id === "tactful")
        ? "tactful"
        : study.stances[0]?.id || "tactful",
    ),
    expressionIntention: oneOf(
      url.searchParams.get("expression-intention"),
      EXPRESSION_INTENTIONS.map(({ id }) => id),
      study.route.intention,
    ),
    expressionRelationship: oneOf(
      url.searchParams.get("expression-relationship"),
      ["all", ...EXPRESSION_RELATIONSHIPS.map(({ id }) => id)],
      "all",
    ),
    expressionRisk: oneOf(
      url.searchParams.get("expression-risk"),
      ["all", ...EXPRESSION_RISKS.map(({ id }) => id)],
      "all",
    ),
  };
}

export function buildShareableHref(
  href: string,
  state: ShareableLocation,
) {
  const url = new URL(href, "https://verbalore.com/");
  for (const key of SHAREABLE_QUERY_KEYS) url.searchParams.delete(key);
  url.searchParams.delete("membership");
  url.hash = "";

  if (state.view === "read") {
    url.searchParams.set("view", "read");
    if (state.workId && READING_BY_ID.has(state.workId)) {
      url.searchParams.set("work", state.workId);
    }
    if (state.browse) url.searchParams.set("browse", "1");
    if (state.browse && state.particular) {
      url.searchParams.set("particular", "1");
    }
    url.searchParams.set("time", state.commitment);
    url.searchParams.set("mood", state.appetite);
    if (state.difficulty) url.searchParams.set("difficulty", state.difficulty);
    if (state.intent) url.searchParams.set("intent", state.intent);
    if (state.form) url.searchParams.set("form", state.form);
    if (state.articulation) url.searchParams.set("landing", state.articulation);
  } else if (state.view === "practice") {
    url.searchParams.set("view", "practice");
    url.searchParams.set("practice", state.practiceView);
    if (state.practiceView === "discover" && state.wordId) {
      url.searchParams.set("word", state.wordId);
      if (state.relatedWordId && state.relatedWordId !== state.wordId) {
        url.searchParams.set("related", state.relatedWordId);
      }
      if (state.wordQuery) url.searchParams.set("word-query", state.wordQuery);
      if (state.wordInitial !== "all") url.searchParams.set("word-initial", state.wordInitial);
      if (state.wordHandle !== "all") url.searchParams.set("word-purpose", state.wordHandle);
      if (state.wordLevel !== "all") url.searchParams.set("word-level", state.wordLevel);
      if (state.wordRegister !== "all") url.searchParams.set("register", state.wordRegister);
      if (state.wordArticulation !== "all") url.searchParams.set("word-landing", state.wordArticulation);
    }
    if (state.practiceView === "expression") {
      url.searchParams.set("study", state.studyId);
      url.searchParams.set("stance", state.stanceId);
      url.searchParams.set("expression-intention", state.expressionIntention);
      if (state.expressionRelationship !== "all") {
        url.searchParams.set("expression-relationship", state.expressionRelationship);
      }
      if (state.expressionRisk !== "all") {
        url.searchParams.set("expression-risk", state.expressionRisk);
      }
    }
  } else if (state.view === "learning") {
    url.searchParams.set("view", "learning");
    url.searchParams.set("section", state.learningSection);
  }

  return `${url.pathname}${url.search}`;
}
